import { useEffect, useRef, useState, useMemo } from "react"
import { useFilterQueryParams } from "../../../hooks/filter_query_params"
import { useMapViewData } from "../../../hooks/map_view_data"
import {
  BaseLayer,
  ALWAYS_SHOW_FILTER,
  getFirstSymbolLayerId,
} from "./base_layer"
import {
  FLOW_CONSTANTS,
  buildFilteredFlowGeoJSON,
} from "../../../utils/flowUtils"

export const CampusFlowKeys = {
  SOURCE_ID: "da-campus-flow-source",
  Layers: {
    FLOW_LINES: "da-campus-flow-lines",
  },
}

export class FlowLineLayer extends BaseLayer {
  constructor(sourceId, layerId) {
    super(
      layerId,
      "line",
      sourceId,
      {
        "line-color": FLOW_CONSTANTS.COLOR,
        "line-opacity": FLOW_CONSTANTS.OPACITY,
        "line-width": [
          "interpolate", ["linear"], ["get", "normalizedVolume"],
          0, FLOW_CONSTANTS.MIN_WIDTH,
          1, FLOW_CONSTANTS.MAX_WIDTH,
        ],
        "line-dasharray": FLOW_CONSTANTS.DASH_PATTERN,
      },
      ALWAYS_SHOW_FILTER
    )
  }
}

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] }

export function CampusFlowSource({ map, isMapLoading }) {
  // TODO: When PR #446 (ENG-3233) merges, campusFlowData will be available from useMapViewData().
  // Until then, this will be undefined and flow lines won't render unless mock data is injected.
  const { campusFlowData } = useMapViewData()
  const { campusFlow, flowThreshold, flowBuilding, days, hour } = useFilterQueryParams()

  const [isLoaded, setIsLoaded] = useState(false)
  const flowLineLayerRef = useRef(
    new FlowLineLayer(CampusFlowKeys.SOURCE_ID, CampusFlowKeys.Layers.FLOW_LINES)
  )
  const animationRef = useRef(null)
  const dashOffsetRef = useRef(0)
  // Build GeoJSON from flow data (pure computation in useMemo)
  const geojson = useMemo(
    () => buildFilteredFlowGeoJSON(campusFlowData, campusFlow, flowThreshold, flowBuilding, days, hour),
    [campusFlowData, campusFlow, flowThreshold, flowBuilding, days, hour]
  )

  // Initialize map source and layer
  useEffect(() => {
    if (!map || isMapLoading || isLoaded) return

    if (!map.getSource(CampusFlowKeys.SOURCE_ID)) {
      map.addSource(CampusFlowKeys.SOURCE_ID, {
        type: "geojson",
        data: EMPTY_GEOJSON,
        promoteId: "id",
      })
    }

    const firstSymbolId = getFirstSymbolLayerId(map)
    flowLineLayerRef.current.addToMap(map, firstSymbolId, ALWAYS_SHOW_FILTER)
    setIsLoaded(true)
  }, [map, isMapLoading, isLoaded])

  // Update source data when GeoJSON changes
  useEffect(() => {
    if (!map || !isLoaded || !map.getSource(CampusFlowKeys.SOURCE_ID)) return

    map.getSource(CampusFlowKeys.SOURCE_ID).setData(geojson)
  }, [map, isLoaded, geojson])

  // Toggle layer visibility based on campusFlow toggle
  useEffect(() => {
    if (!map || !isLoaded) return

    flowLineLayerRef.current.setVisibility(map, campusFlow)
  }, [map, isLoaded, campusFlow])

  // Animate dash pattern
  useEffect(() => {
    if (!map || !isLoaded || !campusFlow) {
      // Stop animation
      if (animationRef.current) {
        clearTimeout(animationRef.current)
        animationRef.current = null
      }
      return
    }

    const frameInterval = Math.round(1000 / FLOW_CONSTANTS.ANIMATION_FPS)
    const dashLength = FLOW_CONSTANTS.DASH_PATTERN[0]
    const gapLength = FLOW_CONSTANTS.DASH_PATTERN[1]
    const totalLength = dashLength + gapLength

    function animate() {
      dashOffsetRef.current = (dashOffsetRef.current + 0.5) % totalLength

      const offset = dashOffsetRef.current
      // Shift the dash pattern to create movement
      const dashArray = [
        Math.max(0.01, dashLength - offset),
        gapLength,
        offset,
        0,
      ]

      flowLineLayerRef.current.setPaintProperty(map, "line-dasharray", dashArray)
      animationRef.current = setTimeout(animate, frameInterval)
    }

    animate()

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current)
        animationRef.current = null
      }
    }
  }, [map, isLoaded, campusFlow])

  // Zoom is handled centrally by useMapData — see flowZoomBounds useMemo

  return null
}
